import { z } from 'zod';
import { emailSchema, isValidPhoneForCountry } from '../lib/validation';
import { COUNTRY_SEED, type SupportedCountryIso } from '../lib/countries.seed';

// AB#1615 — login identifier is "email or phone", not strictly email.
// Previously the field was validated with `emailSchema` which surfaced an
// inline "Invalid email address" error and blocked RHF submit when a phone
// number was typed. Combined with the v2 phone-only mandate (AB#1576) this
// made every owner login attempt with a phone number silently fail.
//
// We now accept any non-empty string here; the BE is the source of truth
// for what's a valid identifier (it normalises phone via libphonenumber
// and resolves email via FormCredentials.Email). Specific shape errors
// (invalid_channel, invalid_credentials, email_not_verified once AB#1612
// ships) come back from the BE and are surfaced by LoginForm's catch
// handler.
export const loginSchema = z.object({
  email: z.string().min(1, 'Enter your email or phone number'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export type LoginForm = z.infer<typeof loginSchema>;

// ── Signup OTP flow (AB#1513 / AB#1453 / AB#1451 / PO-D-012) ────────────────
//
// Step 1 captures: country, phone (the OTP identifier), full name, email,
// and terms.
// OTP is sent over SMS (channel='sms') because Twilio Verify is the only
// production-wired channel today. SendGrid email-OTP was deferred (Task
// AB#1413). Country selection drives phone-prefix and per-country validation.
//
// AB#1630 (2026-05-04) — email is now REQUIRED at Step 1 for active
// countries (was previously optional + duplicated again at Step 3). Email
// stays optional for inactive (waitlist) countries because the waitlist
// stub also captures it as a "let-me-know" hook with no register call.
//
// Inactive countries (e.g. KSA in the launch stub) are blocked at submit
// with the `country_not_supported` error code so the UI can surface a
// "Coming soon — waitlist" message instead of a hard error.

const SUPPORTED_ISO: readonly SupportedCountryIso[] = COUNTRY_SEED.map((c) => c.iso2);

/** Step 1: country + phone + full name + email.
 *
 * CR-2026-05-05b — `termsAccepted` checkbox dropped. Replaced with passive
 * disclosure copy under the Send code CTA (Booksy / Fresha pattern). PO
 * validated against UAE PDPL Art 6-7 + Jordan PDPL Law 24/2023; the
 * informed-consent gate moves from explicit-checkbox to prominent-disclosure.
 */
export const signupStep1Schema = z
  .object({
    country: z.enum(SUPPORTED_ISO as unknown as [SupportedCountryIso, ...SupportedCountryIso[]], {
      required_error: 'Please select your country',
    }),
    // 2026-05-06 — `identifier` and `fullName` were previously declared as
    // `z.string().min(N, ...)` and `z.string().min(2).max(100)`, which
    // triggers the RHF v7.72 controlled-input freeze (Controller silently
    // drops onChange writes when the field schema chains a refinement on
    // z.string() — see AB#1568 + the OTP fix from 2026-05-05). Pure
    // z.string() per field, all length/required checks moved into the
    // superRefine block below.
    identifier: z.string(),
    fullName: z.string(),
    email: z.string().optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    // ── fullName ────────────────────────────────────────────────────────
    if (data.fullName.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fullName'],
        message: 'Full name must be at least 2 characters',
      });
    } else if (data.fullName.length > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fullName'],
        message: 'Full name is too long',
      });
    }
    // ── identifier (phone) ──────────────────────────────────────────────
    if (data.identifier.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identifier'],
        message: 'Phone number is required',
      });
    } else if (!isValidPhoneForCountry(data.identifier, data.country)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identifier'],
        message: 'phone_invalid',
      });
    }
    // AB#1630 — email required from first appearance for all countries
    // (CR-2026-05-05a removed the active-country qualifier).
    const email = (data.email ?? '').trim();
    if (email.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'email_required',
      });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'email_invalid',
      });
    }
  });

export type SignupStep1Form = z.infer<typeof signupStep1Schema>;

/** Step 2: OTP code entry.
 *
 * Accepts 4-6 digits: real Twilio Verify codes are 6 digits, the App Store /
 * Google Play reviewer bypass numbers (Spec Lock Q13, see appsettings.json
 * Auth.TestNumberBypass.Code) use a 4-digit code. The backend remains the
 * sole authority on whether the value is correct — the FE just guards shape.
 *
 * 2026-05-05 — schema simplified to PURE z.string() + superRefine (same
 * workaround as signupStep3Schema). The previous `.regex(...)` chained on
 * `z.string()` triggered the RHF v7.72 controlled-input freeze where
 * Controller silently drops onChange writes — the OTP textbox became
 * non-editable in production after the AB#1660 / AB#1516 deploys. The
 * superRefine pattern enforces the same shape (4-6 digits) without
 * touching the schema chain that confuses RHF.
 */
export const signupStep2Schema = z
  .object({
    code: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!/^\d{4,6}$/.test(data.code)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['code'],
        message: 'Code must be 4 to 6 digits',
      });
    }
  });

export type SignupStep2Form = z.infer<typeof signupStep2Schema>;

/** Step 3 (AB#1568, redesigned in AB#1630): password capture before
 * /v1/auth/register. Email is captured ONCE at Step 1 (AB#1630) and carried
 * forward via component state — Step 3 used to ask for it again, which
 * was the duplicate-email bug.
 *
 * The phone is already OTP-verified at this point; the resulting
 * signup_scoped Bearer is carried as a header on register so the BE
 * marks the credential phone-verified and skips email-link verification.
 * Email is the primary login identifier going forward.
 *
 * 2026-04-28 — Step 3 schema kept as PURE z.string() per field with
 * cross-field .refine() validation. This was originally a workaround for
 * an RHF v7.72 bug where Controllers for fields whose schema chains
 * `.min(N)` silently dropped onChange writes. With the email field gone
 * (AB#1630), only password + confirmPassword remain — both worked under
 * the workaround so we keep the pattern for safety.
 */
// CR-2026-05-07a Phase 3E rev 4 — password complexity hardened per
// founder spec: at least one uppercase, one lowercase, one digit, one
// special character, minimum 8 chars.
//
// Special-char allowlist intentionally permissive (most printable ASCII
// punctuation) so users can pick whatever they remember; we only require
// AT LEAST ONE be present. If a user types only digits / only letters,
// the schema rejects with a typed error code the FE maps to localized
// guidance.
export const signupStep3Schema = z
  .object({
    step3Password: z.string(),
    step3ConfirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    const pwd = data.step3Password;
    if (pwd.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['step3Password'],
        message: 'password_too_short',
      });
    } else if (!/[a-z]/.test(pwd)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['step3Password'],
        message: 'password_missing_lowercase',
      });
    } else if (!/[A-Z]/.test(pwd)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['step3Password'],
        message: 'password_missing_uppercase',
      });
    } else if (!/\d/.test(pwd)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['step3Password'],
        message: 'password_missing_digit',
      });
    } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pwd)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['step3Password'],
        message: 'password_missing_special',
      });
    }
    if (data.step3Password !== data.step3ConfirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['step3ConfirmPassword'],
        message: 'password_mismatch',
      });
    }
  });

export type SignupStep3Form = z.infer<typeof signupStep3Schema>;

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
